import { Card, CardContent, Typography, Box } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import type { ReactNode } from "react";

export interface KPICardProps {
  title: string;
  value: number | string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  unit?: string;
 icon?: ReactNode;
  alert?: "warning" | "error";
  subtitle?: string;
}

export function KPICard({
  title,
  value,
  trend,
  unit = "",
  icon,
  alert,
  subtitle,
}: KPICardProps) {
  // Determine card background color based on alert
  const getBgColor = () => {
    if (alert === "error") return "rgba(244, 67, 54, 0.05)"; // Red tint
    if (alert === "warning") return "rgba(255, 152, 0, 0.05)"; // Orange tint
    return "background.paper";
  };

  // Determine border color
  const getBorderColor = () => {
    if (alert === "error") return "error.main";
    if (alert === "warning") return "warning.main";
    return "divider";
  };

  return (
    <Card
      sx={{
        height: "100%",
        bgcolor: getBgColor(),
        borderLeft: 4,
        borderColor: getBorderColor(),
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent>
        {/* Header with Icon and Title */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: "primary.main", opacity: 0.7 }}>
              {icon}
            </Box>
          )}
        </Box>

        {/* Main Value */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{
              direction: "ltr",
              textAlign: "left",
            }}
          >
            {typeof value === "number" ? value.toLocaleString("en-US") : value}
            {unit && (
              <Typography
                component="span"
                variant="h6"
                sx={{ ml: 1, color: "text.secondary", fontWeight: 400 }}
              >
                {unit}
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Trend Indicator */}
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {trend.isPositive ? (
              <TrendingUp sx={{ fontSize: 18, color: "success.main" }} />
            ) : (
              <TrendingDown sx={{ fontSize: 18, color: "error.main" }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: trend.isPositive ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {Math.abs(trend.value).toFixed(1)}%
            </Typography>
          </Box>
        )}

        {/* Subtitle */}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
